import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: "Competition",
      links: [
        { name: "Prizes", path: "/prizes" },
        { name: "Rules", path: "/rules" },
        // { name: "Register", path: "/register" },
      ],
    },
    {
      title: "Support",
      links: [
        { name: "Contact Us", path: "/contact" },
        // { name: "Technical Help", path: "/contact?category=technical" },
        // { name: "Partnerships", path: "/contact?category=partnership" },
      ],
    },
    {
      title: "Community",
      links: [
        {
          name: "Discord",
          path: "https://discord.gg/xauShxdx6u",
        },
        {
          name: "itch.io",
          path: "https://levelstair.itch.io/",
        },
        {
          name: "YouTube",
          path: "https://www.youtube.com/@LEVELSTAIR",
        },
        {
          name: "X (Twitter)",
          path: "https://x.com/levelstair",
        },
        {
          name: "Facebook",
          path: "https://www.facebook.com/LEVELSTAIR",
        },
        {
          name: "Instagram",
          path: "https://instagram.com/levelstair",
        },
        {
          name: "LinkedIn",
          path: "https://www.linkedin.com/company/levelstair/",
        },
        {
          name: "Reddit",
          path: "https://www.reddit.com/r/LEVELSTAIR/",
        },
      ],
    },
  ];

  return (
    <footer className="bg-exe-bg-secondary/50 border-t border-exe-text-accent/20 mt-auto relative z-10 pt-5">
      <div className="exe-container py-12">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Section - Takes 2 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="flex flex-col">
                <span className="text-3xl font-black text-exe-text-accent tracking-wider">
                  EXE
                </span>
                <span className="text-sm text-exe-text-secondary uppercase tracking-widest leading-none">
                  Sri Lanka's Flagship Game Dev Event
                </span>
              </div>
            </Link>

            <p className="exe-text text-exe-text-muted mb-6 max-w-md">
              Where innovation meets opportunity. Join developers from across
              the nation to create groundbreaking games and compete for
              life-changing prizes.
            </p>

            {/* Community Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <motion.a
                href="https://discord.gg/xauShxdx6u"
                target="_blank"
                rel="noopener noreferrer"
                className="exe-btn exe-btn-primary text-sm px-4 py-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Join Discord
              </motion.a>
              <motion.a
                href="https://www.levelstair.com"
                target="_blank"
                rel="noopener noreferrer"
                className="exe-btn exe-btn-outline text-sm px-4 py-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Visit LEVELSTAIR
              </motion.a>
            </div>

            {/* Social Media Icons */}
            <div className="flex flex-wrap gap-3">
              {[
                {
                  label: "Discord",
                  href: "https://discord.gg/xauShxdx6u",
                  icon: <ion-icon name="logo-discord"></ion-icon>,
                },
                {
                  label: "itch.io",
                  href: "https://levelstair.itch.io/",
                  icon: <ion-icon name="game-controller-outline"></ion-icon>,
                },
                {
                  label: "YouTube",
                  href: "https://www.youtube.com/@LEVELSTAIR",
                  icon: <ion-icon name="logo-youtube"></ion-icon>,
                },
                {
                  label: "X (Twitter)",
                  href: "https://x.com/levelstair",
                  icon: <ion-icon name="logo-twitter"></ion-icon>,
                },
                {
                  label: "Facebook",
                  href: "https://www.facebook.com/LEVELSTAIR",
                  icon: <ion-icon name="logo-facebook"></ion-icon>,
                },
                {
                  label: "Instagram",
                  href: "https://instagram.com/levelstair",
                  icon: <ion-icon name="logo-instagram"></ion-icon>,
                },
                {
                  label: "LinkedIn",
                  href: "https://www.linkedin.com/company/levelstair/",
                  icon: <ion-icon name="logo-linkedin"></ion-icon>,
                },
                {
                  label: "Reddit",
                  href: "https://www.reddit.com/r/LEVELSTAIR/",
                  icon: <ion-icon name="logo-reddit"></ion-icon>,
                },
              ].map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-exe-bg-primary/50 rounded-lg flex items-center justify-center text-exe-text-muted hover:text-exe-text-accent hover:bg-exe-text-accent/20 transition-all duration-150"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={social.label}
                >
                  <span className="text-lg">{social.icon}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Footer Links - Each takes 1 column */}
          {footerLinks.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: (sectionIndex + 1) * 0.1 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h3 className="font-semibold text-exe-text-primary mb-4 text-lg">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.path.startsWith("http") ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="exe-text text-exe-text-muted hover:text-exe-text-accent transition-colors duration-150 block"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="exe-text text-exe-text-muted hover:text-exe-text-accent transition-colors duration-150 block"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Newsletter Signup
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.24 }}
          viewport={{ once: true }}
          className="exe-glass-card p-8 mb-8"
        >
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="exe-heading text-exe-text-accent mb-3 text-2xl">
              Stay Updated with EXE
            </h3>
            <p className="exe-text text-exe-text-muted mb-6">
              Get the latest updates about competition dates, announcements, and exclusive developer resources.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="your.email@example.com"
                className="flex-1 px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary"
              />
              <motion.button
                className="exe-btn exe-btn-primary whitespace-nowrap px-6 py-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </motion.div> */}

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          viewport={{ once: true }}
          className="pt-6 border-t border-exe-text-accent/20"
        >
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="exe-text text-exe-text-muted text-center lg:text-left">
              © {currentYear} EXE by <a className="hover:text-exe-primary" href="https://www.levelstair.com" target="_blank" rel="noopener noreferrer">LEVELSTAIR</a>. All Rights
              Reserved.
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a
                href="https://exe.levelstair.com/#"
                className="text-exe-text-muted hover:text-exe-text-accent transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="https://exe.levelstair.com/#"
                className="text-exe-text-muted hover:text-exe-text-accent transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="https://exe.levelstair.com/#"
                className="text-exe-text-muted hover:text-exe-text-accent transition-colors"
              >
                Code of Conduct
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
